import { Component, Input, OnInit, signal } from '@angular/core';
import { forkJoin } from 'rxjs';
import { AdminClient, VoucherDistributionCycleManagementDetailsDto } from 'src/shared/services/api.service';

@Component({
  selector: 'cert-distribution-cycle-card',
  standalone: false,
  templateUrl: './distribution-cycle-card.component.html',
  styles: `
    .distribution-cycle-card {
      transition:
        transform 0.15s ease,
        border-color 0.15s ease;
    }
    .distribution-cycle-card:hover {
      transform: scale(1.02);
      border-color: var(--cycle-main) !important;
    }
  `,
})
export class DistributionCycleCardComponent implements OnInit {
  @Input() distributionCycleId!: number;
  @Input() includeHoverEffects = true;
  @Input() displayVoucherProgramName = true;
  @Input() cycleIndex = 0;
  @Input() voucherProgramId: number | null = null;

  private resolvedCycleIndexSignal = signal<number | null>(null);
  get resolvedCycleIndex(): number {
    return this.resolvedCycleIndexSignal() ?? this.cycleIndex;
  }

  cycle = signal<VoucherDistributionCycleManagementDetailsDto | undefined>(undefined);

  constructor(private readonly adminClient: AdminClient) {}

  ngOnInit() {
    this.fetchCycleDetails();
  }

  fetchCycleDetails() {
    if (this.voucherProgramId) {
      forkJoin({
        cycle: this.adminClient.getExistingVoucherDistributionCycleDetailsForManagement(this.distributionCycleId),
        program: this.adminClient.getVoucherProgramDetails(this.voucherProgramId),
      }).subscribe(({ cycle, program }) => {
        this.cycle.set(cycle);
        const idx = program.voucherDistributionCycles.findIndex(
          (c) => c.voucherDistributionCycleId === this.distributionCycleId,
        );
        this.resolvedCycleIndexSignal.set(idx >= 0 ? idx : 0);
      });
    } else {
      this.adminClient
        .getExistingVoucherDistributionCycleDetailsForManagement(this.distributionCycleId)
        .subscribe((cycle) => {
          this.cycle.set(cycle);
        });
    }
  }

  get statusClass(): string {
    switch (this.cycle()?.statusString) {
      case 'Active':
        return 'badge-success border border-success';
      case 'NotStarted':
        return 'badge-warning border border-warning text-dark';
      default:
        return 'badge-secondary border border-secondary';
    }
  }

  get allocatedPct(): number {
    const cycle = this.cycle();
    return cycle && cycle.fundingLimit > 0 ? Math.min(100, (cycle.allocatedAmount / cycle.fundingLimit) * 100) : 0;
  }

  get currentAssignedPct(): number {
    const cycle = this.cycle();
    return cycle && cycle.fundingLimit > 0
      ? Math.min(100, (cycle.currentAssignedAmount / cycle.fundingLimit) * 100)
      : 0;
  }

  get redeemedPct(): number {
    const cycle = this.cycle();
    return cycle && cycle.fundingLimit > 0 ? Math.min(100, (cycle.redeemedAmount / cycle.fundingLimit) * 100) : 0;
  }
}
